import { Slugger } from "../../slug";
import type { RailExtensionTocGroup } from "../../rail-extension-context";
import type { ArticleTocItem } from "./types";

/**
 * Reduce a raw markdown heading to its rendered TEXT — the same text the renderer
 * shows (and slugs in the rehype phase). Strips link/image syntax to their label,
 * and emphasis/code/strikethrough markers, so `### [Configure API](url)` slugs to
 * `configure-api` (matching the rendered `<a>Configure API</a>`), not
 * `configure-api-url`. Without this the on-this-page anchor for any heading
 * containing inline markdown would miss the rendered id.
 */
function headingText(raw: string): string {
  return raw
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1") // links / images → label
    .replace(/`([^`]+)`/g, "$1") // inline code
    .replace(/\*\*([^*]+)\*\*/g, "$1") // bold
    .replace(/__([^_]+)__/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1") // italic
    .replace(/_([^_]+)_/g, "$1")
    .replace(/~~([^~]+)~~/g, "$1") // strikethrough
    .trim();
}

/**
 * Derive the on-this-page outline from a markdown body — H2/H3 only, slugged in
 * render order with the SAME `Slugger` the renderer uses, so the rail anchors
 * match the rendered heading ids. THE single derivation that replaces help's
 * server `deriveHeadings` and docs' `extractTocItems`. Fenced code blocks are
 * skipped (a `## foo` inside a fence is code, not a heading — and the renderer
 * doesn't emit a heading for it either, so we must agree).
 */
export function deriveToc(md: string): ArticleTocItem[] {
  const slugger = new Slugger();
  const out: ArticleTocItem[] = [];
  let inFence = false;
  for (const raw of md.split("\n")) {
    const line = raw.trim();
    if (/^(```|~~~)/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    const m = /^(#{2,3})\s+(.+)$/.exec(line);
    if (!m) continue;
    const level = (m[1]!.length === 2 ? 2 : 3) as 2 | 3;
    const text = headingText(m[2]!);
    out.push({ level, text, slug: slugger.slug(text) });
  }
  return out;
}

/**
 * Fold the flat H2/H3 outline into the rail's group→children shape: each H2
 * opens a group, following H3s nest under it (an orphan leading H3 becomes its
 * own group so nothing is dropped). The shape `ContextRail` consumes.
 */
export function tocGroupsFromItems(items: ArticleTocItem[]): RailExtensionTocGroup[] {
  const groups: RailExtensionTocGroup[] = [];
  for (const it of items) {
    if (it.level <= 2 || groups.length === 0) {
      groups.push({ text: it.text, anchor: it.slug, children: [] });
    } else {
      groups[groups.length - 1]!.children.push({ text: it.text, anchor: it.slug });
    }
  }
  return groups;
}

/** ~200 wpm reading estimate, floored at 1 minute. */
export function readingMinutes(md: string): number {
  const words = md.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}
